import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useBookings } from '@/features/bookings/hooks/useRoomBooking';
import { format, addDays, subDays, isToday } from 'date-fns';
import type { Booking } from '@/features/bookings/types';

/**
 * TodayCalendarWidget Component
 * Displays bookings in chronological timeline format
 */
export function TodayCalendarWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAll, setShowAll] = useState(false);

  // Format selected date for API
  const formattedDate = format(selectedDate, 'yyyy-MM-dd');

  // Fetch bookings for selected date
  const { data: allBookings, isLoading } = useBookings({
    startDate: formattedDate,
    endDate: formattedDate,
    status: 'PENDING,APPROVED', // Only show active bookings (exclude REJECTED and CANCELLED)
  });

  // Navigate to previous day
  const goToPreviousDay = () => {
    setSelectedDate((prev) => subDays(prev, 1));
    setShowAll(false); // Reset when changing dates
  };

  // Navigate to next day
  const goToNextDay = () => {
    setSelectedDate((prev) => addDays(prev, 1));
    setShowAll(false); // Reset when changing dates
  };

  // Handle booking click - navigate to booking modal
  const handleBookingClick = (bookingId: string) => {
    navigate(`/calendar/rooms/booking/${bookingId}`);
  };

  // Get room letter badge (first letter of room name)
  const getRoomBadge = (booking: Booking) => {
    const roomName = booking.room?.name;
    if (!roomName) return '?';
    return roomName.charAt(0).toUpperCase();
  };

  // Sort bookings chronologically by start time
  const allSortedBookings = (allBookings || []).sort((a: Booking, b: Booking) => {
    return a.startTime.localeCompare(b.startTime);
  });

  // Show only first 4 bookings unless expanded
  const bookings = showAll ? allSortedBookings : allSortedBookings.slice(0, 4);
  const hasMore = allSortedBookings.length > 4;
  const hasBookings = allSortedBookings.length > 0;

  if (isLoading) {
    return (
      <div
        className="mx-3 mb-3 p-3 rounded"
        style={{
          backgroundColor: 'var(--theme-surface)',
          border: '1px solid var(--theme-border)',
        }}
      >
        <div className="flex items-center justify-center py-4">
          <span className="text-xs" style={{ color: 'var(--theme-text-muted)' }}>
            Loading...
          </span>
        </div>
      </div>
    );
  }

  if (!hasBookings) {
    return (
      <>
        {/* Date Navigation Buttons */}
        <div className="flex items-center gap-1 mx-3 mb-2">
          <button
            onClick={goToPreviousDay}
            className="flex items-center justify-center rounded hover:opacity-70 transition-opacity"
            style={{
              width: '32px',
              height: '32px',
              backgroundColor: 'var(--theme-surface)',
              border: '1px solid var(--theme-border)',
              color: 'var(--theme-text)',
            }}
            aria-label="Previous day"
          >
            <ChevronLeft size={16} />
          </button>

          <div
            className="flex items-center justify-center flex-1 rounded"
            style={{
              height: '32px',
              backgroundColor: 'var(--theme-surface)',
              border: '1px solid var(--theme-border)',
              fontSize: '11px',
              fontWeight: '600',
              color: 'var(--theme-text)',
            }}
          >
            {isToday(selectedDate) ? 'Today' : format(selectedDate, 'MMM d')}
          </div>

          <button
            onClick={goToNextDay}
            className="flex items-center justify-center rounded hover:opacity-70 transition-opacity"
            style={{
              width: '32px',
              height: '32px',
              backgroundColor: 'var(--theme-surface)',
              border: '1px solid var(--theme-border)',
              color: 'var(--theme-text)',
            }}
            aria-label="Next day"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        <div
          className="mx-3 mb-3 p-3 rounded"
          style={{
            backgroundColor: 'var(--theme-surface)',
            border: '1px solid var(--theme-border)',
          }}
        >
          <p className="text-xs text-center py-2" style={{ color: 'var(--theme-text-muted)' }}>
            No bookings for this day
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Date Navigation Buttons - Flat & Compact */}
      <div className="flex items-center gap-1 mx-3 mb-2">
        <button
          onClick={goToPreviousDay}
          className="flex items-center justify-center rounded hover:opacity-70 transition-opacity"
          style={{
            width: '32px',
            height: '32px',
            backgroundColor: 'var(--theme-surface)',
            border: '1px solid var(--theme-border)',
            color: 'var(--theme-text)',
          }}
          aria-label="Previous day"
        >
          <ChevronLeft size={16} />
        </button>

        <div
          className="flex items-center justify-center flex-1 rounded"
          style={{
            height: '32px',
            backgroundColor: 'var(--theme-surface)',
            border: '1px solid var(--theme-border)',
            fontSize: '11px',
            fontWeight: '600',
            color: 'var(--theme-text)',
          }}
        >
          {isToday(selectedDate) ? 'Today' : format(selectedDate, 'MMM d')}
        </div>

        <button
          onClick={goToNextDay}
          className="flex items-center justify-center rounded hover:opacity-70 transition-opacity"
          style={{
            width: '32px',
            height: '32px',
            backgroundColor: 'var(--theme-surface)',
            border: '1px solid var(--theme-border)',
            color: 'var(--theme-text)',
          }}
          aria-label="Next day"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Main Widget Card - Fixed Height for 4 Lines */}
      <div
        className="mx-3 mb-3 rounded overflow-hidden"
        style={{
          backgroundColor: 'var(--theme-surface)',
          border: '1px solid var(--theme-border)',
        }}
      >
        {/* Bookings List - Fixed Height with Internal Scroll */}
        <div
          className="divide-y overflow-y-auto"
          style={{
            borderColor: 'var(--theme-border)',
            maxHeight: showAll ? '350px' : '220px',
          }}
        >
          {bookings.map((booking) => (
            <div
              key={booking.id}
              onClick={() => handleBookingClick(booking.id)}
              className="p-2 hover:bg-opacity-80 transition-all cursor-pointer"
              style={{
                backgroundColor: 'var(--theme-surface)',
              }}
            >
              <div className="flex items-start gap-2">
                {/* Time */}
                <div
                  className="flex-shrink-0 text-xs font-semibold"
                  style={{
                    color: 'var(--theme-text)',
                    minWidth: '45px',
                  }}
                >
                  {booking.startTime}
                </div>

                {/* Booking Details */}
                <div className="flex-1 min-w-0">
                  {/* Title/Description */}
                  <div
                    className="text-xs font-medium truncate"
                    style={{ color: 'var(--theme-text)' }}
                  >
                    {booking.note || booking.room?.name || 'Booking'}
                  </div>
                  {/* Duration */}
                  <div
                    className="text-[10px] truncate"
                    style={{ color: 'var(--theme-text-muted)' }}
                  >
                    {booking.startTime} - {booking.endTime}
                  </div>
                </div>

                {/* Room Badge + Status Dot */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {/* Room Letter Badge */}
                  <div
                    className="flex items-center justify-center rounded text-[10px] font-bold"
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: 'var(--theme-bg)',
                      border: '1px solid var(--theme-border)',
                      color: 'var(--theme-text-muted)',
                    }}
                    title={booking.room?.name || 'Unknown Room'}
                  >
                    {getRoomBadge(booking)}
                  </div>

                  {/* Status Dot */}
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: booking.status === 'APPROVED' ? '#4CAF50' : '#FFA726',
                    }}
                    title={booking.status}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Show More/Less Button */}
        {hasMore && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="w-full py-2 text-xs font-medium hover:opacity-70 transition-opacity"
            style={{
              backgroundColor: 'var(--theme-bg)',
              color: 'var(--theme-text-muted)',
              borderTop: '1px solid var(--theme-border)',
            }}
          >
            {showAll ? 'Show less' : `Show ${allSortedBookings.length - 4} more`}
          </button>
        )}
      </div>
    </>
  );
}
